/**
 * vCard 3.0 (RFC 2426) generation for "Bæta í tengiliði". Pure functions so they can be unit-tested.
 */

export interface VCardInput {
  fullName: string;
  phone?: string | null;
  email?: string | null;
  org: string;
  note?: string | null;
  title?: string | null;
}

/** Escape a TEXT value: backslash, comma, semicolon and newlines (RFC 2426 §4). */
export function escapeVCardText(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\r|\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

/** Fold a content line at 75 octets (UTF-8), never splitting a multi-byte character. */
export function foldVCardLine(line: string): string {
  const enc = new TextEncoder();
  const out: string[] = [];
  let cur = '';
  let curBytes = 0;
  let limit = 75;
  for (const ch of line) {
    const n = enc.encode(ch).length;
    if (curBytes + n > limit) {
      out.push(cur);
      cur = '';
      curBytes = 0;
      limit = 74; // continuation lines start with one space
    }
    cur += ch;
    curBytes += n;
  }
  out.push(cur);
  return out.join('\r\n ');
}

/** "Jón Þór Jónsson" → family "Jónsson", given "Jón Þór" (Icelandic names: last word is the patronymic). */
export function splitName(fullName: string): { family: string; given: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { family: '', given: parts[0] ?? '' };
  return { family: parts[parts.length - 1]!, given: parts.slice(0, -1).join(' ') };
}

/** Keep digits and a leading + for TEL. */
export function normalizePhone(phone: string): string {
  const trimmed = phone.trim();
  const plus = trimmed.startsWith('+') ? '+' : '';
  return plus + trimmed.replace(/[^\d]/g, '');
}

export function buildVCard(v: VCardInput): string {
  const { family, given } = splitName(v.fullName);
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${escapeVCardText(family)};${escapeVCardText(given)};;;`,
    `FN:${escapeVCardText(v.fullName.trim())}`,
    `ORG:${escapeVCardText(v.org)}`,
  ];
  if (v.title?.trim()) lines.push(`TITLE:${escapeVCardText(v.title.trim())}`);
  if (v.phone?.trim()) lines.push(`TEL;TYPE=CELL,VOICE:${escapeVCardText(normalizePhone(v.phone))}`);
  if (v.email?.trim()) lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardText(v.email.trim())}`);
  if (v.note?.trim()) lines.push(`NOTE:${escapeVCardText(v.note.trim())}`);
  lines.push('END:VCARD');
  return lines.map(foldVCardLine).join('\r\n') + '\r\n';
}

/** File name safe for every OS, keeping Icelandic letters. */
export function vcardFilename(fullName: string): string {
  const base = fullName.trim().replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '').replace(/\s+/g, ' ') || 'tengiliður';
  return `${base}.vcf`;
}
